"use client"

/**
 * Every classification type, built-in and custom, described once: its name,
 * icon, what it is for and how big it is. The overview cards and the
 * secondary panel both read this, so they never list different things.
 */

import { useCategoryDimensions, useQuestionDimensions } from "@/hooks/use-category-dimensions"
import { useClassificationLookups } from "@/hooks/use-classification-lookups"
import { useExamCategories } from "@/hooks/use-exam-categories"
import { useLeoWorkspaceSurface } from "@/hooks/use-leo-workspace-surface"
import type { ClassificationScope } from "@/lib/classification-nav"
import { KIND_COPY, type NewTypeKind } from "@/lib/classification/kinds"
import { inTypeOrder, isCustomTypeDimension, kindOf } from "@/lib/classification/lookups"
import { appliesToProgram, categoriesInDimension } from "@/lib/exam-categories"

export interface ClassificationTypeSummary {
  scope: ClassificationScope
  name: string
  icon: string
  description: string
  count: number
  /** "categories", "standards", "levels". */
  unit: string
  /** A few names from inside, so a card shows what it holds. */
  sample: string[]
  custom: boolean
  /** What sort it is, shown on its card: Content is a hierarchy, Bloom's a scale. */
  kind: NewTypeKind
  /** A hierarchy the admin added: the tree its categories live in. */
  dimensionId?: string
}

export function useClassificationTypes(): ClassificationTypeSummary[] {
  const { categories } = useExamCategories()
  const { dimensions } = useCategoryDimensions()
  const content = useQuestionDimensions()[0]
  const program = useLeoWorkspaceSurface().program ?? "Nursing"
  const { lookups } = useClassificationLookups()
  const contentCategories = content ? categoriesInDimension(categories, content.id) : []
  const standards = dimensions.filter((d) => d.tier === "program" && !isCustomTypeDimension(d) && appliesToProgram(d, program))
  const all: ClassificationTypeSummary[] = [
    {
      scope: "content",
      name: "Content",
      icon: "fa-sitemap",
      description: "The course content tree authors tag a question with.",
      count: contentCategories.length,
      unit: "categories",
      sample: contentCategories.filter((c) => c.parentId === null).map((c) => c.name),
      custom: false,
      kind: "tree",
    },
    {
      scope: "standards",
      name: "Standards",
      icon: "fa-award",
      description: "What the program reports on: licensure, competencies, outcomes.",
      count: standards.length,
      unit: "standards",
      sample: standards.map((s) => s.name),
      custom: false,
      kind: "standard",
    },
    {
      scope: "bloom",
      name: "Bloom's",
      icon: "fa-brain",
      description: "The cognitive level a question asks of the student.",
      count: lookups.bloom.length,
      unit: "levels",
      sample: lookups.bloom.map((l) => l.label),
      custom: false,
      kind: "scale",
    },
    {
      scope: "difficulty",
      name: "Difficulty",
      icon: "fa-gauge",
      description: "How hard the question is meant to be.",
      count: lookups.difficulty.length,
      unit: "levels",
      sample: lookups.difficulty.map((l) => l.label),
      custom: false,
      kind: "scale",
    },
    ...lookups.custom.map((type) => {
      const kind = kindOf(type)
      const tree = kind === "tree"
      const rows = tree && type.dimensionId ? categoriesInDimension(categories, type.dimensionId) : []
      return {
        scope: `type:${type.id}` as ClassificationScope,
        name: type.name,
        icon: KIND_COPY[kind].icon,
        description: type.description || "A classification your program added.",
        count: tree ? rows.length : type.levels.length,
        unit: KIND_COPY[kind].nouns,
        sample: tree ? rows.filter((c) => c.parentId === null).map((c) => c.name) : type.levels.map((l) => l.label),
        custom: true,
        kind,
        ...(tree && type.dimensionId ? { dimensionId: type.dimensionId } : {}),
      }
    }),
  ]
  return inTypeOrder(all, lookups.typeOrder)
}
